import {
  useState
} from "react";

import {
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import {
  Building2,
  Globe2,
  LoaderCircle,
  MapPin,
  MessageCircle,
  Save,
  Truck
} from "lucide-react";

import {
  getAdminBusinessSettings,
  updateAdminBusinessSettings
} from "../services/admin";

export function AdminSettingsPage() {
  const queryClient =
    useQueryClient();

  const settingsQuery =
    useQuery({
      queryKey: [
        "admin",
        "settings"
      ],

      queryFn:
        getAdminBusinessSettings
    });

  const data =
    settingsQuery.data;

  const [form, setForm] =
    useState(() => ({
      businessName:
        data?.businessName ?? "",

      brandName:
        data?.brandName ?? "",

      primaryPhone:
        data?.primaryPhone ?? "",

      supportPhone:
        data?.supportPhone ?? "",

      whatsappNumber:
        data?.whatsappNumber ?? "",

      supportEmail:
        data?.supportEmail ?? "",

      addressLine1:
        data?.addressLine1 ?? "",

      addressLine2:
        data?.addressLine2 ?? "",

      city:
        data?.city ?? "",

      state:
        data?.state ?? "",

      postalCode:
        data?.postalCode ?? "",

      country:
        data?.country ?? "India",

      gstRegistered:
        data?.gstRegistered ?? false,

      gstNumber:
        data?.gstNumber ?? "",

      cin:
        data?.cin ?? "",

      instagramUrl:
        data?.instagramUrl ?? "",

      facebookUrl:
        data?.facebookUrl ?? "",

      youtubeUrl:
        data?.youtubeUrl ?? "",

      websiteUrl:
        data?.websiteUrl ?? "",

      allowCustomShipping:
        data?.allowCustomShipping ??
        true,

      defaultShippingAmount:
        String(
          data?.defaultShippingAmount ??
          "0"
        ),

      freeShippingThreshold:
        data?.freeShippingThreshold ??
        "",

      whatsappFooter:
        data?.whatsappFooter ?? "",

      emailFooter:
        data?.emailFooter ?? ""
    }));

  const mutation =
    useMutation({
      mutationFn: () =>
        updateAdminBusinessSettings({
          businessName:
            form.businessName,

          brandName:
            form.brandName,

          primaryPhone:
            form.primaryPhone || null,

          supportPhone:
            form.supportPhone || null,

          whatsappNumber:
            form.whatsappNumber || null,

          supportEmail:
            form.supportEmail || null,

          addressLine1:
            form.addressLine1 || null,

          addressLine2:
            form.addressLine2 || null,

          city:
            form.city || null,

          state:
            form.state || null,

          postalCode:
            form.postalCode || null,

          country:
            form.country,

          gstRegistered:
            form.gstRegistered,

          gstNumber:
            form.gstRegistered
              ? form.gstNumber || null
              : null,

          cin:
            form.cin || null,

          instagramUrl:
            form.instagramUrl || null,

          facebookUrl:
            form.facebookUrl || null,

          youtubeUrl:
            form.youtubeUrl || null,

          websiteUrl:
            form.websiteUrl || null,

          allowCustomShipping:
            form.allowCustomShipping,

          defaultShippingAmount:
            Number(
              form.defaultShippingAmount || 0
            ),

          freeShippingThreshold:
            form.freeShippingThreshold
              ? Number(
                  form.freeShippingThreshold
                )
              : null,

          whatsappFooter:
            form.whatsappFooter || null,

          emailFooter:
            form.emailFooter || null
        }),

      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [
            "admin",
            "settings"
          ]
        });
      }
    });

  function field(
    key:
      keyof typeof form,
    value:
      string | boolean
  ) {
    setForm(
      (current) => ({
        ...current,
        [key]:
          value
      })
    );
  }

  if (settingsQuery.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoaderCircle
          size={38}
          className="animate-spin text-[#5e3b2c]"
        />
      </div>
    );
  }

  if (
    settingsQuery.isError ||
    !settingsQuery.data
  ) {
    return (
      <div className="rounded-3xl bg-red-50 p-8 text-center text-red-700">
        Business settings could not be loaded.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#70845b]">
          Configuration
        </p>

        <h1 className="mt-2 font-serif text-4xl text-[#38251e]">
          Business Settings
        </h1>

        <p className="mt-3 max-w-2xl text-[#746d65]">
          Manage RiNi contact details,
          WhatsApp communication,
          business address and operational defaults.
        </p>
      </div>

      <section className="rounded-[2rem] bg-white p-6">
        <div className="flex items-center gap-3">
          <Building2
            size={22}
            className="text-[#70845b]"
          />

          <h2 className="font-serif text-2xl text-[#38251e]">
            Business Information
          </h2>
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {[
            ["businessName", "Business Name"],
            ["brandName", "Brand Name"],
            ["primaryPhone", "Primary Phone"],
            ["supportPhone", "Support Phone"],
            ["whatsappNumber", "WhatsApp Number"],
            ["supportEmail", "Support Email"]
          ].map(
            ([key, label]) => (
              <label
                key={key}
              >
                <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                  {label}
                </span>

                <input
                  value={
                    form[
                      key as keyof typeof form
                    ] as string
                  }
                  onChange={(event) =>
                    field(
                      key as keyof typeof form,
                      event.target.value
                    )
                  }
                  className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
                />
              </label>
            )
          )}
        </div>
      </section>

      <section className="rounded-[2rem] bg-white p-6">
        <div className="flex items-center gap-3">
          <MapPin
            size={22}
            className="text-[#70845b]"
          />

          <h2 className="font-serif text-2xl text-[#38251e]">
            Address
          </h2>
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {[
            ["addressLine1", "Address Line 1"],
            ["addressLine2", "Address Line 2"],
            ["city", "City"],
            ["state", "State"],
            ["postalCode", "Pincode"],
            ["country", "Country"]
          ].map(
            ([key, label]) => (
              <label
                key={key}
              >
                <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                  {label}
                </span>

                <input
                  value={
                    form[
                      key as keyof typeof form
                    ] as string
                  }
                  onChange={(event) =>
                    field(
                      key as keyof typeof form,
                      event.target.value
                    )
                  }
                  className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
                />
              </label>
            )
          )}
        </div>
      </section>

      <section className="rounded-[2rem] bg-white p-6">
        <div className="flex items-center gap-3">
          <Truck
            size={22}
            className="text-[#70845b]"
          />

          <h2 className="font-serif text-2xl text-[#38251e]">
            Shipping & Tax
          </h2>
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <label className="flex items-center gap-3 rounded-xl bg-[#faf8f5] p-4">
            <input
              type="checkbox"
              checked={
                form.allowCustomShipping
              }
              onChange={(event) =>
                field(
                  "allowCustomShipping",
                  event.target.checked
                )
              }
            />

            <span className="text-sm font-semibold text-[#38251e]">
              Allow custom shipping per order
            </span>
          </label>

          <label className="flex items-center gap-3 rounded-xl bg-[#faf8f5] p-4">
            <input
              type="checkbox"
              checked={
                form.gstRegistered
              }
              onChange={(event) =>
                field(
                  "gstRegistered",
                  event.target.checked
                )
              }
            />

            <span className="text-sm font-semibold text-[#38251e]">
              GST Registered
            </span>
          </label>

          <label>
            <span className="mb-2 block text-sm font-semibold text-[#38251e]">
              Default Shipping Amount
            </span>

            <input
              type="number"
              min="0"
              value={
                form.defaultShippingAmount
              }
              onChange={(event) =>
                field(
                  "defaultShippingAmount",
                  event.target.value
                )
              }
              className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
            />
          </label>

          <label>
            <span className="mb-2 block text-sm font-semibold text-[#38251e]">
              Free Shipping Threshold
            </span>

            <input
              type="number"
              min="0"
              value={
                form.freeShippingThreshold
              }
              onChange={(event) =>
                field(
                  "freeShippingThreshold",
                  event.target.value
                )
              }
              className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
            />
          </label>

          {form.gstRegistered && (
            <label>
              <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                GST Number
              </span>

              <input
                value={
                  form.gstNumber
                }
                onChange={(event) =>
                  field(
                    "gstNumber",
                    event.target.value
                  )
                }
                className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
              />
            </label>
          )}

          <label>
            <span className="mb-2 block text-sm font-semibold text-[#38251e]">
              CIN
            </span>

            <input
              value={
                form.cin
              }
              onChange={(event) =>
                field(
                  "cin",
                  event.target.value
                )
              }
              className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
            />
          </label>
        </div>
      </section>

      <section className="rounded-[2rem] bg-white p-6">
        <div className="flex items-center gap-3">
          <Globe2
            size={22}
            className="text-[#70845b]"
          />

          <h2 className="font-serif text-2xl text-[#38251e]">
            Website & Social Media
          </h2>
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {[
            ["websiteUrl", "Website URL"],
            ["instagramUrl", "Instagram URL"],
            ["facebookUrl", "Facebook URL"],
            ["youtubeUrl", "YouTube URL"]
          ].map(
            ([key, label]) => (
              <label
                key={key}
              >
                <span className="mb-2 block text-sm font-semibold text-[#38251e]">
                  {label}
                </span>

                <input
                  value={
                    form[
                      key as keyof typeof form
                    ] as string
                  }
                  onChange={(event) =>
                    field(
                      key as keyof typeof form,
                      event.target.value
                    )
                  }
                  className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
                />
              </label>
            )
          )}
        </div>
      </section>

      <section className="rounded-[2rem] bg-white p-6">
        <div className="flex items-center gap-3">
          <MessageCircle
            size={22}
            className="text-[#70845b]"
          />

          <h2 className="font-serif text-2xl text-[#38251e]">
            WhatsApp Communication
          </h2>
        </div>

        <label className="mt-6 block">
          <span className="mb-2 block text-sm font-semibold text-[#38251e]">
            WhatsApp Footer
          </span>

          <textarea
            rows={4}
            value={
              form.whatsappFooter
            }
            onChange={(event) =>
              field(
                "whatsappFooter",
                event.target.value
              )
            }
            className="w-full rounded-xl border border-[#5e3b2c]/15 px-4 py-3"
          />
        </label>
      </section>

      {mutation.isError && (
        <div className="rounded-2xl bg-red-50 p-5 text-red-700">
          Settings could not be saved.
        </div>
      )}

      {mutation.isSuccess && (
        <div className="rounded-2xl bg-[#edf1e8] p-5 text-[#526746]">
          Business settings saved successfully.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={
            mutation.isPending
          }
          onClick={() =>
            mutation.mutate()
          }
          className="inline-flex items-center gap-2 rounded-2xl bg-[#5e3b2c] px-6 py-3 font-semibold text-white disabled:opacity-50"
        >
          <Save size={18} />

          {mutation.isPending
            ? "Saving..."
            : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
